import { z } from "zod";
import { createEndpoint, StandardSuccessResponseSchema } from "../createEndpoint";
import type { HttpClient } from "../httpClient";
import { MediaUploadResponseSchema, MediaUploadMultipleResponseSchema } from "../schemas/media";

export interface MediaUploadParams {
    file: File;
    collection?: string;
    metadata?: Record<string, unknown>;
}

export interface MediaUploadMultipleParams {
    files: File[];
    collection?: string;
    metadata?: Record<string, unknown>;
}

function buildFormData(params: MediaUploadParams | MediaUploadMultipleParams): FormData {
    const formData = new FormData();
    
    if ('file' in params) {
        formData.append('file', params.file);
    } else {
        params.files.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
        });
    }
    
    if (params.collection) {
        formData.append('collection', params.collection);
    }
    
    if (params.metadata && Object.keys(params.metadata).length > 0) {
        formData.append('metadata', JSON.stringify(params.metadata));
    }
    
    return formData;
}

export function createMediaApi(http: HttpClient, baseUrl: string) {
    const mediaBaseUrl = `${baseUrl}/media`;
    
    const uploadFormDataEndpoint = createEndpoint<z.infer<typeof MediaUploadResponseSchema>, FormData>(
        http, 
        'POST', 
        '/upload', 
        {
            base: mediaBaseUrl,
            asFormData: true,
            responseSchema: MediaUploadResponseSchema,
        }
    );

    const uploadMultipleFormDataEndpoint = createEndpoint<z.infer<typeof MediaUploadMultipleResponseSchema>, FormData>(
        http, 
        'POST', 
        '/upload-multiple', 
        {
            base: mediaBaseUrl,
            asFormData: true,
            responseSchema: MediaUploadMultipleResponseSchema,
        }
    );
    
    return {
        upload: async (params: MediaUploadParams) => {
            const formData = buildFormData(params);
            return uploadFormDataEndpoint({ body: formData });
        },

        uploadMultiple: async (params: MediaUploadMultipleParams) => {
            const formData = buildFormData(params);
            return uploadMultipleFormDataEndpoint({ body: formData });
        },

        deleteTemporary: createEndpoint<z.infer<typeof StandardSuccessResponseSchema>, undefined, Record<string, never>, { media: string }>(
            http, 
            'DELETE', 
            '/:media/temporary', 
            {
                base: mediaBaseUrl,
                responseSchema: StandardSuccessResponseSchema,
                params: ['media'],
            }
        ),

        delete: createEndpoint<z.infer<typeof StandardSuccessResponseSchema>, undefined, Record<string, never>, { media: string }>(
            http, 
            'DELETE', 
            '/:media', 
            {
                base: mediaBaseUrl,
                responseSchema: StandardSuccessResponseSchema,
                params: ['media'],
            }
        ),
    }
}

export type MediaApi = ReturnType<typeof createMediaApi>;

